'use client';

import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useInView } from '@/lib/useInView';

gsap.registerPlugin(ScrollTrigger);

export default function WhatWeDoSection() {
  const experiencesRef = useRef<HTMLSpanElement>(null);
  const experiencesUnderlineRef = useRef<SVGSVGElement>(null);
  const scaleRef = useRef<HTMLSpanElement>(null);
  const scaleCirclePathRef = useRef<SVGPathElement>(null);
  const advocatesRef = useRef<HTMLSpanElement>(null);
  const advocatesStarPathRef = useRef<SVGPathElement>(null);
  
  const { ref: titleRef } = useInView<HTMLHeadingElement>();
  const { ref: card1Ref } = useInView<HTMLDivElement>();
  const { ref: card2Ref } = useInView<HTMLDivElement>();
  const { ref: card3Ref } = useInView<HTMLDivElement>();

  // Animate underline for "experiences" when it scrolls into view (filled SVG reveal)
  useLayoutEffect(() => {
    if (!experiencesUnderlineRef.current) return;

    const svg = experiencesUnderlineRef.current;

    gsap.set(svg, {
      scaleX: 0,
      transformOrigin: 'left center',
    });

    const context = gsap.context(() => {
      gsap.to(svg, {
        scaleX: 1,
        duration: 1.0,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: experiencesRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate circle around "scale" - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!scaleCirclePathRef.current) return;

    const path = scaleCirclePathRef.current;
    const pathLength = path.getTotalLength();

    // Set up stroke for drawing animation - stroke only
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: '#E60000',
      stroke: '#E60000',
      strokeWidth: 2,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: scaleRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate star after "advocates" - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!advocatesStarPathRef.current) return;

    const path = advocatesStarPathRef.current;
    const pathLength = path.getTotalLength();

    // Set up stroke for drawing animation - stroke only
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 2.5,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.8,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: advocatesRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  return (
    <section className="global-padding">
      <h2 ref={titleRef} className="animate-in-view body-xl pb-[1.0625rem] lg:pb-[1.25rem] text-left border-b border-black">What we do</h2>
      <div className="space-y-0">
        <div ref={card1Ref} className="animate-in-view flex flex-col md:flex-row md:items-center gap-6 px-0 py-[2.5rem] lg:px-[3.125rem] lg:py-[5rem] border-b border-black">
          <div className="md:w-1/2">
            <p className="eyebrow-lg havas-red mb-4">Experience Innovation</p>
            <h3 className="body-xl-display">Strategy for tomorrow’s <span ref={experiencesRef} className="relative inline-block">experiences
              <svg
                ref={experiencesUnderlineRef}
                className="absolute -bottom-1 left-0 w-full h-auto max-w-[12.5rem] md:max-w-[19.625rem]"
                width="314"
                height="12"
                viewBox="0 0 314 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                preserveAspectRatio="none"
              >
                <path d="M0.58992 7.01009C8.3499 -0.887563 97.3139 0.480471 116.474 -2.9719e-05C177.86 0.562441 239.762 0.735731 300.565 6.08763C304.616 7.08719 314.536 6.04355 313.737 9.59957C311.536 13.0827 303.686 9.99884 299.159 9.99549C241.16 4.64266 182.065 4.73008 123.471 4.01648C99.4795 4.02997 75.4865 4.14868 51.5444 5.09799C36.5503 6.04503 19.8913 5.3369 6.05002 8.9727C4.89019 10.0764 3.03197 10.1836 1.6888 9.75493C0.226481 9.28819 -0.646626 8.17891 0.58992 7.01009Z" fill="#E60000" />
              </svg>
            </span></h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-sm">We build the strategic roadmap for a modern brand experience, mapping journeys, designing systems, and setting a clear direction for meaningful growth. The result is a plan that closes the promise-delivery gap, removes roadblocks, and makes every moment work.</p>
          </div>
        </div>
        <div ref={card2Ref} className="animate-in-view flex flex-col md:flex-row md:items-center gap-6 px-0 py-[2.5rem] lg:px-[3.125rem] lg:py-[5rem] border-b border-black">
          <div className="md:w-1/2">
            <p className="eyebrow-lg havas-red mb-4">Experience Design</p>
            <h3 className="body-xl-display">Design and creative for moments that <span ref={scaleRef} className="relative inline-block">scale
              <svg
                className="absolute left-[calc(50%+0.3125rem)] top-[calc(50%+0.8125rem)] -translate-x-1/2 -translate-y-1/2 w-[6.25rem] h-[4.375rem] lg:w-[9.875rem] lg:h-[6.9375rem] pointer-events-none"
                width="274"
                height="193"
                viewBox="0 0 274 193"
                fill="#E60000"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  ref={scaleCirclePathRef}
                  d="M162.884 192.989C160.029 192.287 159.767 189.045 162.452 188.09C172.419 184.786 182.151 181.157 191.869 177.519C216.374 168.064 242.006 156.844 254.121 138.663C285.097 93.8101 260.919 30.3083 190.035 11.7494C157.187 0.0521392 118.117 6.19341 85.6219 15.3783C52.1709 25.3196 23.972 44.2747 13.8214 68.7228C-6.37499 105.705 15.7991 156.06 74.6331 167.929C88.6082 170.36 103.042 168.694 116.938 167.037C122.046 167.217 134.856 162.463 135.602 168.082C135.602 169.396 134.279 170.837 132.184 170.837C126.185 171.35 120.265 172.26 114.292 172.908C79.9114 178.806 46.3032 169.703 25.2555 150.333C-9.64937 120.554 -6.15233 76.5119 22.1514 45.0131C38.353 27.7329 63.867 15.3063 91.5551 8.03939C133.716 -2.28911 168.176 -4.36021 207.743 12.0105C267.311 34.1173 287.088 89.5149 265.556 131.63C256.217 153.881 229.262 169.207 200.775 179.986C188.372 184.326 176.086 190.414 162.884 192.998L162.884 192.989Z"
                  fill="#E60000"
                />
              </svg>
            </span></h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-sm">We create extraordinary experiences, designing, prototyping, and building across product and content. We then integrate, optimize with AI, and measure what matters. What you get is an experience that meets today's high bar, making an impact while reaching new audiences. </p>
          </div>
        </div>
        <div ref={card3Ref} className="animate-in-view flex flex-col md:flex-row md:items-center gap-6 px-0 py-[2.5rem] lg:px-[3.125rem] lg:py-[5rem]">
          <div className="md:w-1/2">
            <p className="eyebrow-lg havas-red mb-4">Membership & Loyalty</p>
            <h3 className="body-xl-display">Experiences that turn<br/>customers into<br/><span ref={advocatesRef} className="relative inline-block">advocates
              <svg
                className="advocates-asterisk"
                width="94"
                height="93"
                viewBox="0 0 94 93"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  ref={advocatesStarPathRef}
                  d="M83.2496 65.609C75.4733 58.162 66.0917 53.0137 56.5327 48.2024C68.577 48.6463 80.6243 49.1043 92.5465 50.9248C93.1908 50.9968 93.7664 50.4059 93.8874 49.8179C94.1419 47.7139 90.3894 48.6164 88.9951 48.1096C77.007 46.5398 64.9102 46.2513 52.8357 45.7694C60.7535 35.398 69.3644 25.1366 79.2348 15.2511C81.0235 13.421 78.796 11.9417 76.7996 13.8931C69.6254 21.1399 63.0156 28.5717 56.8288 36.1146C54.9501 38.504 53.0941 40.896 51.2566 43.2879C49.7969 34.4224 47.9167 -0.852774 40.4114 0.0157605C39.9446 0.0841431 39.6767 0.306936 39.5657 0.589336C39.2726 1.30969 39.9975 2.40117 41.006 2.21539C43.5174 5.78929 43.9831 10.4626 45.0167 14.6317C46.7924 24.3155 47.7056 34.1396 49.0429 43.8858C49.0718 44.0944 49.1051 44.3058 49.1368 44.5101C47.3424 43.6135 45.5551 42.7155 43.7819 41.7959C38.9358 39.408 34.1205 36.9729 29.2466 34.6463C26.855 33.8491 15.5217 26.3308 16.0855 32.2308C16.2016 32.4841 16.3374 32.7258 16.4788 32.9589C17.0559 33.8926 17.8207 34.6981 18.5008 35.5584C19.4874 36.6801 21.6038 35.3882 21.1533 34.0171C28.9717 37.765 36.7664 41.5586 44.5276 45.4222C35.5127 45.1456 0.592249 40.2246 0.00199229 47.7512C-0.0177598 48.2217 0.149912 48.527 0.402947 48.6923C1.05054 49.1153 2.26073 48.6206 2.2781 47.5917C6.26538 45.8146 10.9441 46.2574 15.2373 46.0447C25.0819 46.1656 34.895 47.1592 44.7211 47.7208C45.7417 47.7694 46.758 47.8151 47.7786 47.8637C41.5516 56.1145 35.6005 64.3927 30.0431 72.6954C28.1353 76.0755 21.5158 83.9905 21.9446 86.3878C22.7124 87.0483 23.4756 86.9288 24.1913 86.4233C25.2965 85.6398 26.2794 83.9381 26.9519 82.768C29.4827 78.7056 32.285 74.6492 35.0119 70.5864C39.806 63.6209 44.7176 56.6159 49.9081 49.6522C52.0585 63.5398 54.5028 77.3991 55.0932 91.455C55.1433 92.0996 55.8338 92.5506 56.4377 92.5533C58.5518 92.3982 56.9419 88.8868 57.1754 87.4239C56.3607 74.6106 54.1068 61.9588 52.1794 49.2754C60.3702 53.493 68.8507 57.398 76.024 63.2579C77.8432 64.3787 80.3392 67.9855 82.2665 68.051C83.3691 67.9083 84.1922 66.4327 83.2496 65.609Z"
                  fill="#E60000"
                  strokeLinecap="round"
                />
              </svg>
            </span></h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-sm">
            We make the entire customer journey work in concert, connecting CRM, first-party data, brand systems, and AI-powered measurement into one connected environment. The outcome: powerful experiences that enable personalization across channels while inspiring loyalty. 
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

